import { reviewDecisionLabels, REVIEW_DECISIONS } from '../../label-maps';
import type { GrantOrigin, ReviewDecision } from '../../types';
import type { TFunction } from 'i18next';

// Every option. Used for sizing the column, which has to fit the longest label on
// any row — not just the ones this row offers. '' is the cleared state; the
// design-system Select has no separate empty value.
export const decisionOptions = (
  t: TFunction<'accessGrants'>,
): { value: string; label: string }[] => [
  { value: '', label: '—' },
  ...REVIEW_DECISIONS.map((decision) => ({
    value: decision,
    label: reviewDecisionLabels(t)[decision],
  })),
];

/**
 * The decisions offered for one grant, shared by the table cell and the slide-over
 * so the two can't drift.
 *
 * Trusting is external-only. It exists to stop a grant contributing to its asset's
 * untrusted-external-access finding, and an internal grant has no such finding, so
 * on one it suppresses nothing.
 *
 * A grant already trusted keeps the option even once it reads as internal, which
 * happens when an RCP starts blocking it. Dropping it there would leave the Select
 * showing a value absent from its own list, hiding the decision on the one grant
 * whose trust most needs to be visible.
 */
export function decisionOptionsFor(
  t: TFunction<'accessGrants'>,
  grantOrigin: GrantOrigin,
  currentDecision: ReviewDecision | null,
): { value: string; label: string }[] {
  if (grantOrigin === 'ext' || currentDecision === 'trust_until_review') {
    return decisionOptions(t);
  }
  return decisionOptions(t).filter(
    (option) => option.value !== 'trust_until_review',
  );
}

export function toDecision(value: string): ReviewDecision | null {
  return value === '' ? null : (value as ReviewDecision);
}

// "Review later" with no date set reads as "never" once the reviewer moves on.
// The table fills in a default date and autosaves; the slide-over blocks the
// save instead, since that form is explicit about when it writes.
export function isReviewLaterWithoutDate(
  decision: ReviewDecision | null,
  nextReviewAt: string | null,
): boolean {
  return decision === 'review_later' && !nextReviewAt;
}

// The API refuses "Trust until review" without a review date — the trust window
// would have no end. Unlike "Review later" there is no sane default to fill in:
// how long a grant may go without review is the reviewer's call, so both controls
// make them pick.
export function isTrustWithoutDate(
  decision: ReviewDecision | null,
  nextReviewAt: string | null,
): boolean {
  return decision === 'trust_until_review' && !nextReviewAt;
}
